/**
 * The survey-wide countdown, as one hook (spec 35).
 *
 * ONE source of truth: a single `TimerSnapshot` of what the server last said, plus `nowMs`. The
 * remaining time is COMPUTED from that pair on every render — there is deliberately no
 * `secondsLeft` state being decremented independently, because a second counter drifts from the
 * server on every reload, pause and network hiccup, and reconciling the two is exactly the class
 * of bug this module was asked to avoid.
 *
 * The nudge's auto-dismiss is derived the same way, from when it was raised — there is no second
 * copy of "is it still showing" to fall out of step. The one timeout involved decides nothing: it
 * exists only to advance `nowMs` at the moment the mark falls due, for the case where the 1Hz tick
 * is not running to do it (a paused clock stops the tick, and a mark can outlive the pause).
 */
import { type MutableRefObject, useEffect, useRef, useState } from 'react';

import {
  BEFORE_HARD_STOP_TIMEOUT_MS,
  EXPIRY_RETRY_INTERVAL_MS,
  HEARTBEAT_FAILURES_BEFORE_FALLBACK,
  HEARTBEAT_INTERVAL_MS,
  HEARTBEAT_PROBE_INTERVAL_MS,
  IDLE_REPORT_FLOOR_MS,
  MAX_EXPIRY_REQUEST_FAILURES,
  NUDGE_VISIBLE_MS,
  REQUEST_STALL_TIMEOUT_MS,
  VISIBILITY_BEAT_MIN_INTERVAL_MS,
  type HardStopAsk,
  type SurveyTimerConfig,
  type TimerSnapshot,
  configWithServerView,
  expiryRetryDelayMs,
  hardStopTickNeeded,
  isTerminalExpiryRefusal,
  isTerminalStartRefusal,
  isUnrecoverableRequestFailure,
  nudgeCopyFor,
  nudgeDecision,
  onScreenSignature,
  readoutState,
  remainingSeconds,
  shouldRequestExpiry,
  startRetryDelayMs,
  welcomeBackMessage,
} from './surveyTimer';
import {
  type ExpireTimerResult,
  type SessionActivity,
  type SurveyTimerView,
  expireSurveyTimer,
  sendTimerHeartbeat,
  startSurveyTimer,
} from '../../utils/surveyApi';

/**
 * What a ping says when this hook has no clock to protect — an untimed survey, or the welcome and
 * consent screens before the countdown begins.
 *
 * It used to be safe on the grounds that there was nothing to fold into: the server folded
 * activity only into a session that already had a `timer_state`, and until POST /timer/start
 * landed there was none. That is no longer true. `record_timer_activity` now folds into the
 * EFFECTIVE clock, so a HARD-STOP session whose browser never called /timer/start has one derived
 * from its own `created_at` — the deadline the answer path had been enforcing all along — and
 * this report is folded into it and arms it. (Guidance derives nothing, so for guidance the old
 * reason still holds exactly.)
 *
 * Still the right report, on a narrower argument: it claims presence, and presence can only ever
 * cost the respondent time, never buy them any. `apply_activity` moves `expires_at` on the RESUME
 * branch alone, which needs a stored `paused_at` — and nothing on this path can ever set one, since
 * it never reports `hidden` or an idle stretch. So the deadline cannot move, and the only write it
 * makes is `last_seen_at`, which is honest: the browser demonstrably made this request.
 *
 * What it does forgo is pause credit while the hook is inactive on a hard stop. That is the
 * design's own answer rather than an oversight — a hard stop is anchored on `created_at`
 * precisely so that welcome and consent time IS charged, and `started` is false for exactly those
 * screens. The case it reads oddly in is the one the hook should not be inactive for at all: a
 * tab that loaded the survey untimed pins `config` to null for the page's life and so never shows
 * a countdown either. Filed as #646 rather than closed here: that is a change to the runtime's
 * config plumbing, not to this hook.
 */
const NO_CLOCK_ACTIVITY: SessionActivity = { idle_ms: 0, hidden: false };

/**
 * What counts as the respondent being there.
 *
 * `pointermove` is on the list because the server pauses after three minutes of NO event at all,
 * and every other entry here needs a commit — a press, a keystroke, a scroll. Someone composing a
 * long open-text answer in their head for three minutes before they start typing sends not one of
 * them, so the clock was paused underneath them and they came back to "Welcome back — your timer
 * paused while you were away" without having been anywhere. The same false-idle class the
 * capture-phase PLAYBACK_EVENTS below were added for, through the other door.
 *
 * `wheel` is here for the same reason `scroll` is not enough on its own: `scroll` fires only when
 * the page actually MOVES, so a respondent wheeling over a question shorter than the viewport, or
 * one already at its end stop, produces `wheel` and nothing else. The sibling hook that measures
 * active time already registers it next to `pointermove` (`useActiveQuestionTime.ts`) — this is
 * the same door, and it was open on this side.
 *
 * Both fire at pointer rate, so they must stay as cheap as the others: `mark` stamps a ref and
 * nothing else — no state, no render, no effect.
 */
const ACTIVITY_EVENTS = [
  'pointerdown',
  'pointermove',
  'keydown',
  'input',
  'scroll',
  'wheel',
  'touchstart',
] as const;

/**
 * The completion envelope for a stop the endpoint never got to grant.
 *
 * Stands in for the reply of an ask that died — a 404 for a session already gone, or a run of
 * failures long enough to bound it — on a respondent whose writes the server has ALREADY been
 * discarding. There is nothing left to wait for and no envelope coming, but the caller still has
 * a session to finish: it reads `redirect_url`, finds none, and falls back to the timed-out
 * completion page. `expired` is true because it is — by the only party that can still tell.
 *
 * `credited` is FALSE, and that is the whole of the difference between this and a granted stop.
 * Nothing completed the session: it is still `in_progress` and stays that way until the
 * abandonment sweep takes it, so there is no quota slot, no `redirect_url` and no panel credit.
 * The completion page asserts "your answers have been saved" off this field, and asserting it
 * here told a respondent their session had finished cleanly at the exact moment it had not.
 */
const UNREACHABLE_HARD_STOP: ExpireTimerResult = { expired: true, credited: false };

/**
 * Watching something IS being there.
 *
 * A respondent sitting through a four-minute stimulus video sends not one of the events above,
 * so the server reaches its idle threshold and pauses a clock nobody had left — they are then
 * welcomed back from somewhere they never went, and on a hard stop the survey quietly hands them
 * every video's length back as extra time.
 *
 * Registered in the CAPTURE phase because media events do not bubble: a window listener still
 * sees them on their way down to the element. `timeupdate` is the one that carries the session —
 * it keeps firing for as long as the frame keeps moving.
 */
const PLAYBACK_EVENTS = ['play', 'playing', 'timeupdate'] as const;

interface RaisedNudge {
  message: string;
  /**
   * When it started being shown — or null while it is still waiting for somewhere to be shown.
   *
   * A mark raised while the runtime has no nudge slot (inside a rapid-fire block, on its intro,
   * behind the AI spinner) used to be recorded as seen and then auto-dismissed unseen: a
   * five-minute warning crossed inside a four-minute rapid-fire block was gone for good and the
   * respondent was never told. It waits instead.
   */
  raisedAtMs: number | null;
}

const nudgeShowing = (raised: RaisedNudge | null, nowMs: number): boolean =>
  raised !== null && raised.raisedAtMs !== null && nowMs - raised.raisedAtMs < NUDGE_VISIBLE_MS;

/**
 * Wait for `work`, but never longer than `timeoutMs` — `true` if it settled inside that window,
 * `false` if the window closed with it still in flight.
 *
 * The one shape every request on this path shares. A rejection was always handled; a STALL was
 * not, because nothing in `surveyApi` carries an `AbortController`, so a hung request simply never
 * settles — and each of the three places below latches a ref BEFORE its request leaves and gives
 * it back only when something comes home. Silence therefore latched all three for the life of the
 * page: no clock at all, no hard stop, no last write.
 *
 * Deliberately bounds the WAIT rather than cancelling the work, which is why the answer is a
 * boolean and not a value. The request may well have reached the server — /timer/expire in
 * particular may already have completed the session — so callers keep their own handler on the
 * original promise and honour whatever it eventually says. This decides one thing only: whether to
 * stop waiting and ask again.
 */
const settledWithin = (work: Promise<unknown>, timeoutMs: number): Promise<boolean> =>
  new Promise<boolean>((resolve) => {
    const id = window.setTimeout(() => resolve(false), timeoutMs);
    void Promise.resolve(work)
      .catch(() => {})
      .then(() => {
        window.clearTimeout(id);
        resolve(true);
      });
  });

/**
 * Forget what the server said about a config that is no longer the one in force.
 *
 * Each of these records a conclusion about a PARTICULAR clock: that the ask was made, that it was
 * refused for good, how long to leave it before asking again. A researcher editing the survey
 * under a live tab replaces the thing all three describe, and a conclusion kept across that edit
 * is not caution but a stuck latch — `requested` in particular is what stops a second ask ever
 * being made, so a hard stop restored after a refusal was never enforced again.
 *
 * Declared out here, taking its refs, rather than inside the hook: a function declared in the
 * component body and CALLED from another one costs that caller its inferred stability, and every
 * effect reading `applyTimer` then wants it in a dependency array it must not be in.
 */
const releaseExpiryLatches = (
  requested: MutableRefObject<boolean>,
  unreachable: MutableRefObject<boolean>,
  retryDelayMs: MutableRefObject<number>,
  failures: MutableRefObject<number>,
  asksMade: MutableRefObject<number>,
): void => {
  requested.current = false;
  unreachable.current = false;
  retryDelayMs.current = EXPIRY_RETRY_INTERVAL_MS;
  // The failure run belongs to the old clock too — left standing, a restored hard stop would be
  // given up on again by the very first failure it met.
  failures.current = 0;
  // And so does the ask count. It answers "can the server grant the NEXT ask", which is a fact
  // about a particular deadline: asks made against the old one say nothing about a clock that has
  // just been switched on, extended or restored, whose first ask is refused by construction all
  // over again.
  asksMade.current = 0;
};

interface UseSurveyDeadlineArgs {
  sessionId: number | null;
  config: SurveyTimerConfig | null;
  /** True once the respondent is actually answering — the clock must not run on welcome/consent. */
  started: boolean;
  /**
   * Whether the runtime currently has anywhere to show a nudge. Defaults to true.
   *
   * While false a raised mark waits rather than being spent on an empty screen.
   */
  canShowNudge?: boolean;
  /**
   * True while the respondent is demonstrably engaged in something that fires no input events —
   * recording a voice answer, say. Treated as activity for as long as it lasts.
   */
  engaged?: boolean;
  /**
   * True once a WRITE has come back saying the server's buzzer has already gone.
   *
   * Every respondent-write path answers `status: success` past the hard stop and records nothing,
   * marking it only with `survey_timer_expired`. That makes the server's deadline a fact while
   * ours is still an estimate — and the two can be a long way apart, because the server falls
   * back to a clock derived from the session's own `created_at` when the browser never completed
   * the /timer/start handshake. Ending the session stays with /timer/expire either way; this is
   * what makes the client ask for it instead of waiting for its own clock to catch up.
   */
  serverSaysExpired?: boolean;
  /**
   * The caller's last chance to write anything that still belongs to this session, awaited before
   * the server is asked to end it.
   *
   * /timer/expire COMPLETES the session, and every other session write refuses a session that is
   * not in progress — so a hard stop landing inside a live rapid-fire block has exactly one
   * moment to record it, and this is that moment.
   *
   * Never allowed to block the stop for long: a rejection is swallowed and the ask goes ahead at
   * once, and a promise that simply never settles — which is what a hung fetch looks like, since
   * nothing in `surveyApi` carries an `AbortController` — is waited out for at most
   * BEFORE_HARD_STOP_TIMEOUT_MS before the ask goes ahead anyway.
   *
   * Told which ask it is running for, because it runs before EVERY one of them and the first is
   * refused by construction. A write that merely finishes something already in flight belongs on
   * all of them; one that IRREVERSIBLY ends part of the survey — closing a live rapid-fire block
   * as expired — belongs only on an ask the server can actually grant, or the respondent carries
   * on inside a section the client has already shut behind them.
   */
  beforeHardStop?: (ask: HardStopAsk) => Promise<void> | void;
  /**
   * Called when the server confirms a hard stop, with the completion envelope it returned.
   *
   * That envelope is the SAME one POST /complete returns, `redirect_url` included, and the
   * caller has to honour it: a panel respondent who ran out of time still has to be sent back to
   * the panel, or they are never credited for the survey they just sat through.
   */
  onHardStop: (result: ExpireTimerResult) => void;
}

export interface SurveyDeadline {
  remaining: number | null;
  paused: boolean;
  showReadout: boolean;
  isFinal: boolean;
  /** Transient message, or null. Also carries the welcome-back line after an idle pause. */
  nudge: string | null;
  /**
   * True while this hook is the one pinging /heartbeat.
   *
   * The respondent runtime already sends a plain liveness ping to that same endpoint. Only one of
   * the two may beat at the full cadence: two heartbeats a minute doubles the request rate on the
   * respondent path, and the plain one carries no activity, so the server reads it as "the tab is
   * busy" and resumes a clock this hook had just had paused.
   *
   * Contingent on this hook's own beat actually working. Ownership derived from configuration
   * alone outlived the heartbeat itself: nothing reached the server at all, and the abandonment
   * sweep flipped a respondent who was sitting there answering to `abandoned`.
   *
   * Handing ownership over does NOT silence this hook's beat, and cannot: the liveness ping is a
   * `sendBeacon` and reports nothing, so nothing else could ever discover the channel was back.
   * It drops to HEARTBEAT_PROBE_INTERVAL_MS instead. "Exactly one runs" was the claim for twelve
   * rounds and the code never held it — both kept POSTing every sixty seconds for as long as the
   * failure lasted.
   */
  ownsHeartbeat: boolean;
  /**
   * What this hook would tell the server about the respondent, were it beating right now.
   *
   * Published for the two pings that are exempt from the exclusivity rule above — the unload
   * beacon and the liveness ping that takes over when this hook's own beat keeps failing. Neither
   * may send NOTHING: the route reads a bodyless heartbeat as `idle_ms=None, hidden=False`,
   * `apply_activity` takes its resume branch, and a clock this hook had paused starts draining
   * with nobody there. A ref, so reading it costs the caller no render and no effect dependency.
   */
  activityRef: MutableRefObject<() => SessionActivity>;
}

export function useSurveyDeadline({
  sessionId,
  config: loadedConfig,
  started,
  canShowNudge = true,
  engaged = false,
  serverSaysExpired = false,
  beforeHardStop,
  onHardStop,
}: UseSurveyDeadlineArgs): SurveyDeadline {
  const [snapshot, setSnapshot] = useState<TimerSnapshot | null>(null);
  /**
   * The config as the SERVER last reported it, which outranks the copy the page loaded with.
   *
   * Every reading carries `mode`, `visible` and `duration_seconds` for exactly this — a survey
   * edited mid-session reaching the respondent without a second endpoint. Null until a reading
   * says something different, and never a substitute for the survey having a clock at all: an
   * untimed survey stays untimed.
   */
  const [serverConfig, setServerConfig] = useState<SurveyTimerConfig | null>(null);
  const [nowMs, setNowMs] = useState(0);
  const [raised, setRaised] = useState<RaisedNudge | null>(null);
  const [heartbeatHealthy, setHeartbeatHealthy] = useState(true);
  /** Bumped by a scheduled retry, purely to wake the start effect again. */
  const [startRetryToken, setStartRetryToken] = useState(0);

  const shownRef = useRef<number[]>([]);
  const startAttemptsRef = useRef(0);
  const startRetryTimeoutRef = useRef(0);
  const mountedRef = useRef(true);
  /** The previous tick's reading, so a mark fires only on the tick that watches it go by. */
  const lastRemainingRef = useRef<number | null>(null);
  const lastActivityRef = useRef(0);
  const expiryRequestedRef = useRef(false);
  const expiryAskedAtMsRef = useRef(0);
  /** How long the last refusal said to wait, so the ask is not held back a flat half-minute. */
  const expiryRetryDelayMsRef = useRef(EXPIRY_RETRY_INTERVAL_MS);
  /** Latched by a refusal no retry can get past, so the forced hard-stop tick can stand down. */
  const expiryUnreachableRef = useRef(false);
  /** Failures in a row with nothing coming home, so an ask that can never land is bounded. */
  const expiryFailuresRef = useRef(0);
  /**
   * How many asks have actually LEFT for this clock, which is how the caller knows what its own
   * last write may safely do.
   *
   * Zero means the next ask is the first, and the first is refused by construction: the client
   * asks at its own zero and `should_hard_stop` grants only past the buzzer grace. Counted rather
   * than timed off our own interpolation, because that interpolation is exactly what the server
   * disagrees with — a retry is scheduled from the REFUSAL's reading plus that grace plus a
   * second, so "an ask after one that has already been answered" is the only past-the-boundary
   * signal here that does not rest on the client's own clock being right.
   */
  const expiryAsksMadeRef = useRef(0);
  /**
   * Latched once the session has actually been ended, so it can only be ended once.
   *
   * Not the same latch as `expiryRequested`, which now reopens for a request still hanging when
   * its window closes — which is exactly what can leave two asks in the air at the same time. The
   * server can grant both, and `onHardStop` navigates, so the second grant would send a respondent
   * who has already been sent somewhere else.
   */
  const hardStopSentRef = useRef(false);
  /**
   * The retry's own wake-up, for a session with no clock of ours to tick it.
   *
   * The retry after a refusal normally rides the 1Hz tick — which only runs for a survey THIS
   * browser has a snapshot for. A hard stop the server flagged on a survey we loaded untimed has
   * none, so one refusal was the end of the matter and nothing ever ended the session.
   */
  const expiryWakeTimeoutRef = useRef(0);
  const startedRef = useRef(false);
  const wasPausedRef = useRef(false);
  const beatFailuresRef = useRef(0);
  /** When the last heartbeat LEFT, so a returning tab cannot ask for one per visibility change. */
  const lastBeatAtMsRef = useRef(0);
  /**
   * Whether a beat actually went out saying this tab had gone — and so whether there is a pause of
   * our own making waiting to be resumed.
   *
   * The return beat is exempt from the visibility throttle only while this is set. Without the
   * exemption, a flick away and back inside the throttle window sent the pause and swallowed the
   * resume, leaving "Timer paused" in front of a respondent who was sitting right there until the
   * next interval beat. Without the arming, every return would be exempt and the throttle would
   * mean nothing.
   */
  const beatForHiddenRef = useRef(false);
  /**
   * The heartbeat itself, published so the start can fire the first one the moment it lands.
   *
   * The beat is `setInterval(beat, 60_000)` and nothing else, so the server heard NOTHING from a
   * freshly loaded page for a full minute. Everything the server holds about this session is
   * therefore up to a minute stale on every page load — a config the researcher edited, and above
   * all a PAUSE, which is what a reload of a paused session leaves in force: the readout says
   * "Timer paused" for sixty seconds and the beat that finally resumes it credits a minute onto
   * the deadline and announces "Welcome back" to somebody who never went away.
   *
   * Fired from the start's reply rather than at mount, because before that reply there is nothing
   * to report against — the server folds activity only into a session that already has a
   * `timer_state`, so a beat sent first is a request that tells it nothing — and because the two
   * requests are issued in the same millisecond, which `applyTimer`'s ordering guard cannot
   * separate: a start reply landing second would put its own (paused) replay back over the reading
   * that had just resumed the clock, and leave the pause showing until the next interval anyway.
   */
  const beatRef = useRef<(() => void) | null>(null);
  /**
   * Request time of the newest SNAPSHOT applied, so an overtaken reply can be recognised.
   *
   * Strictly about the snapshot, and the 1Hz tick reads it that way — it stands down when the
   * snapshot it closed over is older than this. So it must not move for a payload that leaves the
   * snapshot alone: a reading the server could not compute advancing it silently retired the tick
   * for the rest of the session, with the countdown frozen wherever it happened to be.
   */
  const lastAppliedRequestMsRef = useRef(0);
  /**
   * The same question for the CONFIG, which travels on the same payloads and is read separately.
   *
   * Its own marker rather than a share of the one above, because the two are adopted under
   * different conditions: every reading carries a config, only a computable one carries a
   * snapshot. One marker can answer for one of them.
   */
  const lastConfigRequestMsRef = useRef(0);
  /** What was last put on screen, so the tick can stay quiet when nothing would change. */
  const lastSignatureRef = useRef('');
  const canShowNudgeRef = useRef(canShowNudge);
  const engagedRef = useRef(engaged);
  const raisedRef = useRef<RaisedNudge | null>(null);
  /**
   * The exact remaining seconds between renders — this hook's own copy, and nobody else's.
   *
   * `remaining` is only as fresh as the last thing that changed on screen, because the calm
   * readout deliberately moves once a minute rather than once a second. The one decision that
   * needs the real figure in between is whether a mark held for a slot is still TRUE by the time
   * it gets one: announcing "just 5 minutes left" on a clock that emptied while the respondent was
   * inside a rapid-fire block is a lie rather than merely stale news.
   *
   * It was returned to the caller for a while, on the strength of the same argument. Nothing ever
   * read it, and offering it was worse than useless: it is maintained by the 1Hz tick, so a caller
   * reading it while the clock is paused, before the first reading lands, or on an untimed survey
   * would get a number this hook is not keeping true — with no way of telling.
   */
  const remainingRef = useRef<number | null>(null);
  const beforeHardStopRef = useRef(beforeHardStop);
  const onHardStopRef = useRef(onHardStop);

  /**
   * The config every decision here is made on: what the server last said, else what loaded.
   *
   * The server's copy can only exist for a survey that HAS a clock, so an untimed survey is null
   * either way and nothing below runs.
   */
  const config = loadedConfig ? (serverConfig ?? loadedConfig) : null;
  const configRef = useRef(config);
  /** Read by the activity report, which must not claim a respondent is idle before the clock. */
  const activeRef = useRef(false);

  // A retry scheduled after this component is gone has nothing left to start, and the request it
  // would make belongs to a session nobody is sitting in — so the unmount latches it off.
  //
  // Set back to true on the way IN as well, because a mount is not necessarily the first one:
  // StrictMode mounts, tears down and mounts again in development, and a flag that only ever went
  // false left the start-retry path dead for the whole session locally. A retry that cannot be
  // exercised where it is written is a retry nobody can tell is broken.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      window.clearTimeout(expiryWakeTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    configRef.current = config;
  }, [config]);
  useEffect(() => {
    canShowNudgeRef.current = canShowNudge;
  }, [canShowNudge]);
  useEffect(() => {
    engagedRef.current = engaged;
  }, [engaged]);
  useEffect(() => {
    raisedRef.current = raised;
  }, [raised]);
  useEffect(() => {
    beforeHardStopRef.current = beforeHardStop;
  }, [beforeHardStop]);
  useEffect(() => {
    onHardStopRef.current = onHardStop;
  }, [onHardStop]);

  const active = Boolean(config && sessionId && started);
  const remaining = remainingSeconds(snapshot, nowMs);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  /**
   * The one place the activity report is built, so the beat and the pings that stand in for it
   * cannot drift apart. Reads refs only, so its identity never has to change.
   */
  const activityRef = useRef<() => SessionActivity>(() => {
    if (!activeRef.current) return NO_CLOCK_ACTIVITY;
    const idleMs = engagedRef.current ? 0 : Date.now() - lastActivityRef.current;
    return {
      idle_ms: idleMs > IDLE_REPORT_FLOOR_MS ? idleMs : 0,
      hidden: document.visibilityState === 'hidden',
    };
  });

  /**
   * Make sure SOMETHING comes back to ask again after a refusal the session can recover from.
   *
   * The ask re-runs on `nowMs`, which the 1Hz tick advances — and that tick only runs for a survey
   * this browser holds a snapshot for. A hard stop the server flagged on a survey we loaded
   * untimed has no snapshot and no tick, so the first `not_due` (or dropped request) was the end of
   * it: nothing ever asked again, and nothing else ends that session either.
   *
   * One timeout, replaced rather than stacked, and timed off the same delay the refusal set — so
   * where the tick IS running this costs at most one extra render per retry interval, and where it
   * is not it is the only thing keeping the deadline alive.
   */
  const wakeForExpiryRetry = () => {
    window.clearTimeout(expiryWakeTimeoutRef.current);
    expiryWakeTimeoutRef.current = window.setTimeout(() => {
      if (mountedRef.current) setNowMs(Date.now());
    }, expiryRetryDelayMsRef.current);
  };

  /**
   * Fold a server reading in. Called only from async callbacks, never during render.
   *
   * `requestedAtMs` is when the request LEFT, not when the answer came back: the server computed
   * that number before the reply crossed the network, so stamping it on arrival credits the
   * respondent with a round trip of time they have already spent.
   *
   * The reading carries the CONFIG too — mode, visibility and duration — shipped on every one of
   * them so a survey edited mid-session reaches the respondent without a second endpoint. Taking
   * it is what makes a hard stop switched on under a live tab enforceable, and what stops a tab
   * switched the other way asking to expire a survey that no longer ends anybody.
   * `configWithServerView` hands back the current config unchanged when nothing usable or nothing
   * different arrived, so an unedited survey costs no state update at all.
   */
  const applyTimer = (timer: SurveyTimerView, requestedAtMs: number) => {
    // The interval beat and the visibility beat overlap, so replies can cross, and a stale reply's
    // view of the SURVEY is stale too.
    //
    // Inline rather than a helper of its own, however much it reads like one: a function declared
    // in the component body and CALLED from here would cost this one its inferred stability, and
    // every effect that reads `applyTimer` then wants it in a dependency array it must not be in.
    // The same reason `releaseExpiryLatches` is declared outside the component.
    if (requestedAtMs >= lastConfigRequestMsRef.current) {
      lastConfigRequestMsRef.current = requestedAtMs;
      const currentConfig = configRef.current;
      const nextConfig = configWithServerView(currentConfig, timer);
      if (nextConfig !== currentConfig) {
        // A changed MODE releases the expiry latches: they record what the server said about the
        // OLD config, and a tab told `survey_not_hard_stop` under guidance would otherwise never
        // ask again once the researcher switched the hard stop back on.
        //
        // A changed DURATION does the same, and takes the nudge bookkeeping with it. Both record a
        // conclusion about a deadline that has just moved: a survey extended from ten minutes to
        // forty puts the five-minute and last-minute marks back in front of the respondent, who
        // crossed them again in silence because `shown` still said they had been told — and a
        // clock switched off and back on need never have shown this tab an explicit clear in
        // between, so the new duration is the only news of it a refused ask will ever get.
        if (nextConfig && currentConfig) {
          const durationChanged = nextConfig.duration_seconds !== currentConfig.duration_seconds;
          if (durationChanged) shownRef.current = [];
          if (durationChanged || nextConfig.mode !== currentConfig.mode) {
            releaseExpiryLatches(
              expiryRequestedRef,
              expiryUnreachableRef,
              expiryRetryDelayMsRef,
              expiryFailuresRef,
              expiryAsksMadeRef,
            );
          }
        }
        configRef.current = nextConfig;
        setServerConfig(nextConfig);
      }
    }
    // The server's remaining_seconds is optional over the wire: a session whose deadline it
    // cannot read reports null. Arithmetic on that silently produces 0, which would show a full
    // survey as out of time and, in hard-stop mode, ask the server to end it.
    //
    // Only the SNAPSHOT is dropped, never the config above it. `public_view` emits
    // `remaining_seconds: None` while still carrying `mode`, `visible` and `duration_seconds` —
    // the reading and the config come from different places, and only the reading can be
    // unreadable — so guarding the whole function on it threw away exactly what those fields are
    // shipped on every heartbeat FOR: a hard stop switched on under a live tab, arriving on the
    // one reply that could not also say how long was left, and silently ignored.
    if (typeof timer?.remaining_seconds !== 'number') return;
    // Applying an overtaken reading rewinds the countdown in front of the respondent: time they
    // watched pass comes back, and then drains a second time.
    if (requestedAtMs < lastAppliedRequestMsRef.current) return;
    lastAppliedRequestMsRef.current = requestedAtMs;
    const next: TimerSnapshot = {
      serverRemainingSeconds: timer.remaining_seconds,
      readAtMs: requestedAtMs,
      paused: timer.paused,
    };
    const now = Date.now();
    // Kept precise here as well as in the tick, and NEVER derived from a render: `remaining` is
    // only as fresh as the last thing that changed on screen, and folding that back in would put
    // a coarsened figure on the one channel that is supposed to carry the exact one.
    remainingRef.current = remainingSeconds(next, now);
    setSnapshot(next);
    setNowMs(now);
  };

  /**
   * Drop the clock, because the server says there is no longer one.
   *
   * An EXPLICIT `timer: null` is not a reply that forgot to mention the timer — the heartbeat
   * resolves its reading against the survey's CURRENT config and returns exactly this once that
   * config is cleared, deliberately, as the fix for a respondent counting down a deleted deadline.
   * Ignoring it left them interpolating a dead clock to zero: "Just 5 minutes left" on a survey
   * with no limit, or — in hard-stop mode, where `isSpentGuidanceClock` does not apply — a red
   * `Final minute 0:00` pinned to the rest of the survey while /timer/expire answers
   * `survey_not_timed`.
   *
   * Ordered like a reading, because it IS one: a clear that a newer reply has already overtaken
   * must not wipe a clock that reply just confirmed.
   */
  const clearTimer = (requestedAtMs: number) => {
    if (requestedAtMs < lastAppliedRequestMsRef.current) return;
    lastAppliedRequestMsRef.current = requestedAtMs;
    // The config marker moves with it: a clear supersedes everything older, and the survey view a
    // stale reply carried describes a clock that is gone.
    lastConfigRequestMsRef.current = Math.max(lastConfigRequestMsRef.current, requestedAtMs);
    remainingRef.current = null;
    lastRemainingRef.current = null;
    // Everything below records a conclusion about the clock that has just been deleted, and a
    // deleted clock can come back — the researcher who cleared it can set another one, at the same
    // length and in the same mode, with nothing in any later reading to say it is a different one.
    // Kept, they outlive it: the marks passed on the old clock stay "already shown" and go by in
    // silence on its replacement, and `expiryRequested` — left taken by the `survey_not_timed`
    // refusal the deletion caused — means that tab never asks to be ended by the restored hard
    // stop at all.
    shownRef.current = [];
    releaseExpiryLatches(
      expiryRequestedRef,
      expiryUnreachableRef,
      expiryRetryDelayMsRef,
      expiryFailuresRef,
      expiryAsksMadeRef,
    );
    // Both are already null on every heartbeat after the first, and React bails out of an
    // identical state — so a cleared survey costs one render, not one a minute.
    setSnapshot(null);
    raisedRef.current = null;
    setRaised(null);
  };

  // --- activity tracking -------------------------------------------------------------------
  useEffect(() => {
    if (!active) return;
    const mark = () => {
      lastActivityRef.current = Date.now();
    };
    mark();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    PLAYBACK_EVENTS.forEach((e) =>
      window.addEventListener(e, mark, { capture: true, passive: true }),
    );
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, mark));
      PLAYBACK_EVENTS.forEach((e) => window.removeEventListener(e, mark, { capture: true }));
    };
  }, [active]);

  // An engagement that fires no events at all — a voice answer being recorded — bounds the idle
  // report on both sides: zero while it runs (below), and a fresh mark the moment it ends.
  useEffect(() => {
    if (!engaged) return;
    lastActivityRef.current = Date.now();
    return () => {
      lastActivityRef.current = Date.now();
    };
  }, [engaged]);

  // --- start the server clock: once if it lands, again on a bounded backoff if it does not ---
  // A failed start used to be permanent — the latch was taken before the request and never given
  // back — and /heartbeat carries no timer for a session the server never started one for. One
  // dropped request therefore left the survey with no clock at all, and in hard-stop mode with no
  // deadline at EITHER end, since the server's own check reads the same timer state that call
  // would have created. The respondent then runs past the researcher's limit unstopped.
  useEffect(() => {
    if (!active || !sessionId || startedRef.current) return;
    startedRef.current = true;
    const requestedAtMs = Date.now();
    /**
     * Only the FIRST outcome of this request may decide anything about the next one.
     *
     * The same guard, and the same reason, as `retryDecided` on the expiry path below. THREE
     * outcomes reach `retryStart` for one POST — a 200 with no clock in it, a rejection, and the
     * stall watchdog at fifteen seconds — and the last two both arrive for a request that hangs
     * and then fails. Making the timeout replace rather than stack stopped the superseded wake
     * firing, but both outcomes still DECIDED: the attempt counter advanced twice for one failed
     * request, so the backoff skipped a step, and where the watchdog's retry had already gone out
     * the second call gave `startedRef` back underneath it and the next wake started a third
     * request against the same session.
     *
     * Bounded, because /timer/start is idempotent and hands back the ORIGINAL deadline. But it is
     * still two live starts and a backoff that lies about how many times it has tried, and the
     * comment on the expiry path's guard has asserted this one exists since the round that wrote
     * it.
     */
    let retryDecided = false;
    // Give the latch back and wake this effect again later. Never a guessed deadline: until one
    // of these lands there is still no clock, only another ask.
    //
    // The `clearTimeout` here is belt to the guard's braces, and measurably so: with the guard in
    // place it never once had a live timeout to clear across the whole timer suite — 27
    // executions, 0 of them pending. It cannot, either. A closure schedules at most one wake and
    // is latched the instant it does, and a newer closure can only exist because that wake
    // already fired (it is what bumped the token) or because a dependency changed, which runs
    // this effect's cleanup and clears it. Kept anyway: a deadline path should not rest its "one
    // pending wake" invariant on React's cleanup ordering alone.
    const retryStart = () => {
      if (!mountedRef.current || retryDecided) return;
      retryDecided = true;
      window.clearTimeout(startRetryTimeoutRef.current);
      startedRef.current = false;
      startAttemptsRef.current += 1;
      startRetryTimeoutRef.current = window.setTimeout(
        () => setStartRetryToken((token) => token + 1),
        startRetryDelayMs(startAttemptsRef.current),
      );
    };
    const request = startSurveyTimer(sessionId);
    request
      .then(({ timer, reason }) => {
        // A 200 carrying no `timer` is not a clock — but it is not necessarily a dropped request
        // either, and the difference is the whole of it. The endpoint REFUSES with 200 and
        // `{timer: null, reason}`; a respondent resuming a session the 15-minute abandonment sweep
        // already flipped gets `session_not_in_progress` every time, so retrying it is a POST a
        // minute for the life of the page, on the unauthenticated respondent path, beside a
        // /heartbeat that is 404ing for the same reason.
        //
        // Anything else — an empty or reshaped envelope, a reason we do not recognise — is treated
        // as a failure to start and retried. The latch was already taken, so leaving it taken
        // would mean no clock at all, and in hard-stop mode no deadline at EITHER end, since the
        // server's own check reads the timer state this call would have created.
        if (!timer) {
          if (!isTerminalStartRefusal(reason)) retryStart();
          return;
        }
        // Taken whenever it lands, and NOT conditional on this particular effect run still being
        // the current one. A `cancelled` flag dropped a SUCCESSFUL reply here while `startedRef`
        // — which only `retryStart` ever gives back — stayed taken, so the effect's own early
        // return blocked every further attempt and the session ran unclocked with nothing left to
        // retry it. StrictMode tears every effect down once, so in development that was every
        // mount. `applyTimer` is already ordering-guarded, so a reply a retry has overtaken is
        // dropped there, on the one comparison that can actually tell.
        if (!mountedRef.current) return;
        applyTimer(timer, requestedAtMs);
        // Now that there IS a clock, tell the server who is sitting in front of it — once, here,
        // rather than waiting out the first sixty-second interval with a stale pause on screen.
        beatRef.current?.();
      })
      // A rejection carrying a 4xx is the server saying this session has no clock to start and
      // never will — a 404 for one that has been deleted or purged. Retried like everything else
      // it is a POST a minute for the life of the page, on the unauthenticated respondent path.
      // Anything else, status or none, is still treated as a request that did not land.
      .catch((error: unknown) => {
        if (!isUnrecoverableRequestFailure(error)) retryStart();
      });
    // The third outcome, and the only one neither handler above can see: a `fetch` with no
    // `AbortController` behind it that never answers and never fails. The latch was taken before
    // the request left, and /heartbeat carries no timer at all for a session the server never
    // started one for — so a hung POST left the survey with no clock for the rest of the session,
    // and in hard-stop mode no deadline at EITHER end, since the server's own check reads the
    // timer state this call would have created. The reply is still taken above if it ever lands.
    void settledWithin(request, REQUEST_STALL_TIMEOUT_MS).then((settled) => {
      if (!settled) retryStart();
    });
    return () => {
      window.clearTimeout(startRetryTimeoutRef.current);
    };
  }, [active, sessionId, startRetryToken]);

  // --- heartbeat: the only channel, and the server decides ---------------------------------
  useEffect(() => {
    if (!active || !sessionId) return;
    const beat = async () => {
      const requestedAtMs = Date.now();
      lastBeatAtMsRef.current = requestedAtMs;
      try {
        const { timer } = await sendTimerHeartbeat(sessionId, activityRef.current());
        beatFailuresRef.current = 0;
        setHeartbeatHealthy(true);
        // Three cases, not two. A reading is folded in; an EXPLICIT null is the server saying the
        // researcher's clock is gone and is obeyed; a reply with no `timer` key at all carries no
        // news about it, and the snapshot stands.
        if (timer) applyTimer(timer, requestedAtMs);
        else if (timer === null) clearTimer(requestedAtMs);
      } catch {
        // Keep the last snapshot and keep interpolating. A dropped heartbeat must not look like
        // time vanishing, and must never end the session. What it must do is hand the liveness
        // ping back once it stops being a blip and starts being an outage.
        beatFailuresRef.current += 1;
        if (beatFailuresRef.current >= HEARTBEAT_FAILURES_BEFORE_FALLBACK)
          setHeartbeatHealthy(false);
      }
    };
    // One a minute while the channel works, one every five while the liveness ping is covering
    // for it. Both cannot run at a minute: they POST the SAME endpoint, so a persistent failure
    // doubled the request rate on the unauthenticated respondent path for the life of the page.
    // Standing this one down altogether is not the alternative — it is the only channel that can
    // discover the outage is over, since the liveness ping is a `sendBeacon` that reports nothing.
    const id = window.setInterval(
      beat,
      heartbeatHealthy ? HEARTBEAT_INTERVAL_MS : HEARTBEAT_PROBE_INTERVAL_MS,
    );
    // Published for the start to fire once its own reply lands. Nothing else calls it, and the
    // cleanup takes it back, so a reply arriving after this effect was torn down cannot beat for
    // a session nobody is in.
    beatRef.current = () => {
      void beat();
    };
    /**
     * Report the tab going away as well as coming back.
     *
     * The server pauses INSTANTLY on `hidden` — no idle threshold — so the transition is news in
     * its own right. Only the return was reported, and the return says `hidden: false`, so a tab
     * hidden for less than an interval was never reported hidden at all and the whole absence was
     * charged to the deadline. (Longer ones self-heal: the return's `idle_ms` crosses the
     * three-minute threshold and the server backdates the pause. It is exactly the SHORT
     * interruption — the common one — that was silently billed.)
     *
     * The return beat is then exempt from the throttle, and has to be. Under it, a flick away and
     * back inside ten seconds sent the pause and swallowed the resume, so a respondent sitting
     * right there watched "Timer paused" until the next interval beat up to a minute later. The
     * exemption is armed only by a hidden beat that actually went out, and spent on the first
     * return, so the pattern is strictly one pause plus one resume — at worst two requests per
     * throttle window rather than one, and nothing at all for a tab that never hides.
     */
    const onVisibility = () => {
      const hidden = document.visibilityState === 'hidden';
      if (!hidden && beatForHiddenRef.current) {
        beatForHiddenRef.current = false;
        void beat();
        return;
      }
      // Beat on return so the clock is right before they read it rather than a minute stale — but
      // not once per alt-tab. Unthrottled this is a request per visibility change on top of the
      // interval, and someone flicking between tabs while they think produces them as fast as
      // they can switch.
      if (Date.now() - lastBeatAtMsRef.current < VISIBILITY_BEAT_MIN_INTERVAL_MS) return;
      beatForHiddenRef.current = hidden;
      void beat();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.clearInterval(id);
      beatRef.current = null;
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [active, sessionId, heartbeatHealthy]);

  // --- the one-second tick, and the nudge decision that rides on it -------------------------
  // Both live in the interval callback rather than in an effect body: the threshold crossing is
  // a consequence of time passing, so this is where it belongs, and it keeps render pure.
  useEffect(() => {
    if (!active || !snapshot || snapshot.paused) return;
    const id = window.setInterval(() => {
      const cfg = configRef.current;
      if (!cfg) return;
      // A newer reading has superseded the snapshot this interval closed over. The effect will be
      // torn down for it on the next render, but this callback was already scheduled — and a tick
      // that fires in between would publish a figure from the old snapshot onto `remainingRef`,
      // which is the one channel carrying the exact number and which nothing else would correct.
      // Most visible after a CLEAR: the readout goes, and the ref keeps a deleted deadline.
      if (snapshot.readAtMs < lastAppliedRequestMsRef.current) return;
      const now = Date.now();
      const remainingNow = remainingSeconds(snapshot, now);
      // Kept current every second whether or not this tick re-renders anything, so the decisions
      // that read it between renders — whether a held nudge is still true, above all — are made
      // against the clock rather than against the last thing that reached the screen.
      remainingRef.current = remainingNow;
      // Decided against the PREVIOUS reading, so a mark speaks only on the tick that watches it
      // go by. Everything already behind us — a reload part-way through, a tab the browser
      // throttled, a heartbeat correcting the clock — is recorded silently instead.
      const { raise, seen } = nudgeDecision(
        lastRemainingRef.current,
        remainingNow,
        cfg.duration_seconds,
        shownRef.current,
      );
      lastRemainingRef.current = remainingNow;
      shownRef.current = seen;

      let showing = nudgeShowing(raisedRef.current, now);
      if (raise !== null && cfg.visible) {
        // With nowhere to show it the mark waits (raisedAtMs null). A more urgent mark going by
        // meanwhile simply replaces it, which is the same rule the visible path follows: only
        // the mark the respondent is actually inside may speak.
        const next: RaisedNudge = {
          message: nudgeCopyFor(raise),
          raisedAtMs: canShowNudgeRef.current ? now : null,
        };
        raisedRef.current = next;
        setRaised(next);
        showing = next.raisedAtMs !== null;
      }

      // Re-render the runtime only when this second can change something on screen — or when a
      // hard stop is close enough that landing it on time depends on this tick.
      const signature = onScreenSignature(remainingNow, cfg, showing);
      if (
        signature !== lastSignatureRef.current ||
        hardStopTickNeeded(remainingNow, cfg.mode, expiryUnreachableRef.current)
      ) {
        lastSignatureRef.current = signature;
        setNowMs(now);
      }
    }, 1000);
    return () => window.clearInterval(id);
  }, [active, snapshot]);

  // --- a held nudge, once there is somewhere to show it -------------------------------------
  useEffect(() => {
    if (!canShowNudge) return;
    const pending = raisedRef.current;
    if (!pending || pending.raisedAtMs !== null) return;
    const now = Date.now();
    // A mark may only speak while it is still TRUE. Once the clock is spent there is nothing
    // left to announce, and announcing it anyway would be a lie rather than merely stale news.
    const spent = remainingRef.current !== null && remainingRef.current <= 0;
    const next = spent ? null : { ...pending, raisedAtMs: now };
    raisedRef.current = next;
    setRaised(next);
    setNowMs(now);
  }, [canShowNudge]);

  // --- the wake-up its dismissal needs when the tick is not running --------------------------
  // Auto-dismiss stays derived from (raisedAtMs, nowMs) — there is still only one rule for when a
  // mark stops being shown. What this adds is a guarantee that something advances `nowMs` at the
  // moment it falls due: the 1Hz tick stands down while the clock is paused, so a mark raised
  // within its visible window of a pausing heartbeat used to stay pinned until the NEXT one, up
  // to a minute later — still claiming a countdown the pause had already invalidated.
  //
  // One timeout per raised mark, cleared when it is replaced or the hook unmounts.
  useEffect(() => {
    if (!raised || raised.raisedAtMs === null) return;
    const dueInMs = raised.raisedAtMs + NUDGE_VISIBLE_MS - Date.now();
    // Scheduled even when it is already due — a mark raised behind a long stall is dismissed on
    // the next turn of the loop rather than synchronously here, which would cascade a render.
    const id = window.setTimeout(() => setNowMs(Date.now()), Math.max(0, dueInMs));
    return () => window.clearTimeout(id);
  }, [raised]);

  // --- welcome back, after an idle pause ----------------------------------------------------
  // Without this the number appears to jump and reads as a bug; it also quietly says the clock
  // was fair while they were away.
  useEffect(() => {
    if (!snapshot) return;
    if (snapshot.paused) {
      wasPausedRef.current = true;
      return;
    }
    if (!wasPausedRef.current) return;
    wasPausedRef.current = false;
    const cfg = configRef.current;
    if (!cfg?.visible) return;
    // The same rounding-down rule the readout follows, and for the same reason: this line quotes a
    // number to a respondent who has just come back and is deciding whether to carry on, and on a
    // hard stop rounding to nearest offers them up to thirty seconds that are not there. "About"
    // hedges the figure, not the deadline behind it.
    //
    // The copy lives in `welcomeBackMessage` rather than here because the rule has a second half
    // this call site kept getting wrong: `wholeMinutesLeft` floors at 1, which is right only
    // where `formatRemaining` has already taken the sub-minute case away, and there is no such
    // guard on this path. Composed inline, a hard stop resumed with twenty seconds on it greeted
    // the respondent with "About 1 minute left" over a red 0:20.
    const welcomeBack: RaisedNudge = {
      message: welcomeBackMessage(snapshot.serverRemainingSeconds),
      // NOW, not `snapshot.readAtMs`. That stamp is when the heartbeat LEFT, and the mark is
      // dismissed against arrival time — so it began life a whole round trip old, and a round
      // trip longer than the eight seconds it shows for meant it was never seen at all. Which is
      // precisely the case it exists for: someone returning from a sleep on a slow connection.
      raisedAtMs: canShowNudgeRef.current ? Date.now() : null,
    };
    // The ref goes with the state, as it does at every other raise site. The effect below repairs
    // it a render later, which is one render too late for the 1Hz tick and the held-mark release,
    // both of which read the REF: a tick landing in that window judges a mark that is on screen
    // to be absent, and stamps `lastSignatureRef` with a nudge-less signature for a screen that
    // has one. Nothing in the suite could tell the difference — the tick recomputes the signature
    // from scratch every second and the dismissal has its own wake-up, so the ledger is
    // self-correcting — but "every raise writes both" is the invariant the other three sites keep
    // and the one that makes reading this code safe.
    raisedRef.current = welcomeBack;
    setRaised(welcomeBack);
  }, [snapshot]);

  // --- hard stop: ask, never assert --------------------------------------------------------
  // Keyed on `nowMs` as well as `remaining`, because `remaining` stops changing the moment it
  // reaches zero — an effect that only watched it would get exactly one attempt per session and
  // no way back from a refusal or a dropped request.
  //
  // Gated on the SESSION alone, not on the config. `serverSaysExpired` is the server telling us
  // its buzzer has already gone, and the case it was added for is precisely the one where this
  // page has no timer config to check it against: a hard stop switched on under a tab that loaded
  // the survey untimed, which never called /timer/start, so the server falls back to a deadline
  // derived from the session's created_at and starts flagging every write. Requiring a config made
  // that respondent's session unendable — the runtime shows TIME_UP and stops, no /timer/expire is
  // ever sent, no completion happens, and a panel respondent leaves with no redirect and no
  // credit. /timer/expire needs nothing from us but the session id, and re-derives the rest.
  useEffect(() => {
    if (!sessionId) return;
    // The ask has been given up on for good — and the server has since confirmed the stop it could
    // not get granted.
    //
    // Every place that stands the ask down decides whether to finish the session from the
    // `serverSaysExpired` of the instant it runs, and the ordinary sequence is the other way
    // round: the ask fails for five minutes with nothing flagged, is bounded or 404s, and only
    // THEN does the respondent's next answer come back `survey_timer_expired`. That confirmation
    // had nowhere to land — `abandonExpiry` returns before the line that gives `expiryRequested`
    // back, and the terminal-refusal branch never reaches it either — so the session was never
    // completed, `redirect_url` was never fetched, and a panel respondent sat on the escape-less
    // "Closing your survey off" card with no credit for the survey they had just sat through.
    // Which is the exact outcome the round that wrote `abandonExpiry` was fixing, through a door
    // it did not cover.
    //
    // Finished here rather than by re-opening the ask. The two latches say different things:
    // `expiryRequested` is "an ask is out", `expiryUnreachable` is "no ask can ever succeed", and
    // a confirmation arriving on a DIFFERENT channel is no evidence against the second. Releasing
    // them both — the obvious fix — would put a 404 back on a POST every thirty seconds for the
    // life of the page, on the unauthenticated respondent path, which is the loop that latch
    // exists to end. There is nothing left to wait for and no envelope coming, so this is the same
    // completion `abandonExpiry` would have made, held until the only party that can still tell
    // says the buzzer went.
    if (expiryUnreachableRef.current && serverSaysExpired && !hardStopSentRef.current) {
      hardStopSentRef.current = true;
      onHardStopRef.current(UNREACHABLE_HARD_STOP);
      return;
    }
    if (
      !shouldRequestExpiry(
        remaining,
        config?.mode ?? null,
        expiryRequestedRef.current,
        serverSaysExpired,
        expiryUnreachableRef.current,
      )
    )
      return;
    const askedAt = Date.now();
    // Timed off the last refusal's own reading rather than a flat half-minute. The first ask of
    // every hard-stop session is refused — the client asks at its zero, the server grants only
    // past its five-second grace — so a flat interval delayed EVERY hard stop by 30 seconds, and
    // every one of those seconds is a window in which the respondent's answers are accepted and
    // silently dropped.
    if (askedAt - expiryAskedAtMsRef.current < expiryRetryDelayMsRef.current) return;
    expiryRequestedRef.current = true;

    // Whatever still belongs to this session goes first: the ask completes it server-side, and
    // afterwards nothing else can be written to it. Waited on, but never indefinitely — a write
    // that hangs would otherwise hold the stop open for the whole stall, with every retry behind
    // it short-circuited by the latch taken above.
    //
    // `sentAtMs` is stamped when the request actually LEAVES, which is up to the whole wait after
    // `askedAt`. applyTimer's contract is "when the request left", so re-syncing off `askedAt`
    // handed the server's fresh reading a backdate the length of the wait — a hard stop inside a
    // rapid-fire block whose close took eight seconds derived straight back to zero, and the
    // re-sync achieved nothing at all.
    let sentAtMs = askedAt;

    /**
     * Stand the ask down for good — and finish the session on the way out where it is already over.
     *
     * The terminal-refusal branch below has completed the session since the round it was written
     * in: when the server has ALREADY begun discarding this respondent's writes, a /timer/expire
     * that can never succeed leaves them on the "Time's up / Closing your survey off" card with
     * nothing behind it — no completion, no `redirect_url`, and on a panel no credit for the
     * survey they just sat through. That card's own comment claims "/timer/expire either grants
     * the stop or is refused for good, and both now finish the session", and it was true only of
     * the HTTP-200 refusal. These are the other two ways the ask dies, and they say the same thing.
     *
     * Gated on `serverSaysExpired` for exactly the reason the refusal branch is: a dead ask on a
     * session the server never flagged is a hard stop we could not confirm, not one we may assert.
     * Read at the instant this runs, and deliberately so — a flag that goes up LATER is caught by
     * the confirmation at the top of this effect, which makes the same completion off the latch
     * this leaves behind rather than by reopening an ask it has just concluded is dead.
     */
    const abandonExpiry = () => {
      expiryUnreachableRef.current = true;
      if (!mountedRef.current || !serverSaysExpired || hardStopSentRef.current) return;
      hardStopSentRef.current = true;
      onHardStopRef.current(UNREACHABLE_HARD_STOP);
    };

    /**
     * Only the FIRST outcome of this ask may decide anything about the next one.
     *
     * One POST has two ways of reaching the retry path: the stall watchdog at fifteen seconds and
     * the request's own rejection at, say, twenty. Both landed here, so a single slow attempt was
     * counted as two failures and the cap of ten was spent after five real attempts — half the
     * outage the count is meant to sit through, on a deadline that then fails OPEN. The start
     * path carries its own copy of this guard, for its own version of the same fault: making
     * `retryStart` replace rather than stack its wake-up stopped the superseded timeout firing
     * but let both outcomes decide, which double-advanced the backoff and released the start
     * latch under a replacement already in flight.
     *
     * Bounds the RETRY decision only. A grant is honoured however late it lands, and so is a
     * refusal that names itself final: those are news about the session, not about when to ask.
     */
    let retryDecided = false;

    // Let the next tick ask again. Shared by a request that failed and one that is still hanging
    // when its window closes: neither brought back a reading to time the next ask off, so the
    // conservative wait stands for both.
    //
    // Neither is free, either. Both leave the channel unproven, and a channel that is never going
    // to work — a 500 on every attempt, a connection that stalls every time — otherwise costs a
    // POST every thirty seconds for as long as the tab is open. Counted rather than timed, and
    // reset by anything that comes home, so only a run of failures with nothing in between can
    // stand the ask down.
    const askAgainLater = () => {
      if (!mountedRef.current || retryDecided) return;
      retryDecided = true;
      expiryFailuresRef.current += 1;
      if (expiryFailuresRef.current >= MAX_EXPIRY_REQUEST_FAILURES) {
        abandonExpiry();
        return;
      }
      expiryRetryDelayMsRef.current = EXPIRY_RETRY_INTERVAL_MS;
      expiryRequestedRef.current = false;
      wakeForExpiryRetry();
    };

    const send = () => {
      sentAtMs = Date.now();
      // Stamped where the request LEAVES, not where the ask was decided. The retry it throttles is
      // timed off the reading that comes BACK from here, and `beforeHardStop` above can hold the
      // ask for up to BEFORE_HARD_STOP_TIMEOUT_MS in between — so measuring the wait from
      // `askedAt` spent that hold twice over. A submit in flight for three seconds, refused with
      // `remaining_seconds: 0`, asked again at six seconds against a grace that elapses at eight:
      // that ask is refused too, and the stop lands twelve seconds late instead of eight.
      expiryAskedAtMsRef.current = sentAtMs;
      // Counted where the request LEAVES, for the same reason the stamp is: what the next ask may
      // safely be accompanied by depends on an ask having actually been made, not on one having
      // been decided on and then held behind a ten-second wait.
      expiryAsksMadeRef.current += 1;
      const request = expireSurveyTimer(sessionId);
      // The outcome neither handler below can see: a request that never answers and never fails.
      // `expiryRequestedRef` is taken before it leaves and given back only by something coming
      // home, so a hung POST latched it for good — the hard stop never landed, the session was
      // never completed, the panel respondent never got their `redirect_url`, and because
      // `expiryUnreachable` stayed false the forced 1Hz tick went on re-rendering the whole
      // respondent runtime for the life of the page: the exact cost that latch exists to kill.
      //
      // Timed from here rather than from `askedAt`, so a slow-but-alive close above does not eat
      // the budget of the request it was making room for. And only the WAIT is abandoned: this
      // POST may well have reached the server and completed the session, and its reply is the
      // only place `redirect_url` ever appears, so the handler below stays on it and honours it
      // whenever it lands, however late.
      void settledWithin(request, REQUEST_STALL_TIMEOUT_MS).then((settled) => {
        if (!settled) askAgainLater();
      });
      return request;
    };
    // Told which ask this is, because it runs before every one of them and only some of them can
    // be granted. The FIRST ask of a hard stop is refused by construction — the client asks the
    // moment its own interpolation hits zero, and `should_hard_stop` grants only once the deadline
    // is past by MORE than the buzzer grace — so a write that irreversibly ends part of the survey
    // must not ride on it. `serverSaysExpired` is the exception and the reason this is not simply
    // "not the first": the server is already discarding this respondent's writes, which is only
    // true past that same boundary, so that ask can be granted whenever it is made.
    const pending = beforeHardStopRef.current?.({
      grantable: serverSaysExpired || expiryAsksMadeRef.current > 0,
    } satisfies HardStopAsk);
    const ask: Promise<ExpireTimerResult | null> = pending
      ? settledWithin(Promise.resolve(pending), BEFORE_HARD_STOP_TIMEOUT_MS).then(send)
      : send();

    ask
      .then((result) => {
        // Nobody is sitting in this session any more. `onHardStop` sets `window.location.href`
        // from the completion envelope, so a respondent who finished normally and navigated while
        // this ask was in flight would be yanked off whatever page they had reached — by a
        // deadline for a survey they had already completed. The latch is deliberately NOT the
        // per-effect kind: this effect re-runs on every tick, so a cleanup flag would cancel the
        // hard stop on the second after it was asked for and it would never land at all.
        if (!mountedRef.current) return;
        // Something came home, so the channel works — whatever it said. The failure run that
        // stands the ask down counts CONSECUTIVE failures only, or a long session on a flaky
        // connection would be quietly disarmed by ten failures it had already recovered from.
        expiryFailuresRef.current = 0;
        // Once, however many asks are in the air. Reopening the door on a stall is what puts a
        // second one there, and the server can grant both — but `onHardStop` navigates, so the
        // later grant would send a respondent who has already been sent somewhere else.
        if (hardStopSentRef.current) return;
        if (result?.expired) {
          hardStopSentRef.current = true;
          onHardStopRef.current(result);
          return;
        }
        // Refused. Take the reading it sent — it is more authoritative than our interpolation —
        // and keep the door open unless this is a refusal the session can never recover from.
        // The default is the retrying one on purpose: a deadline may fail closed, never open.
        if (result?.timer) applyTimer(result.timer as SurveyTimerView, sentAtMs);
        expiryRetryDelayMsRef.current = expiryRetryDelayMs(result?.timer);
        // A refusal nothing can get past also retires the forced 1Hz tick: it exists only to land
        // this ask, and from here no tick ever will.
        if (isTerminalExpiryRefusal(result?.reason)) {
          expiryUnreachableRef.current = true;
          // Except when the server has ALREADY dropped this respondent's writes. Then the session
          // is over whatever the endpoint says — `session_not_in_progress` here usually means the
          // server ended it on its own side, which is why the write came back expired — and the
          // runtime is sitting on a "time is up" error with nothing behind it. Ending it here
          // costs a respondent who cannot continue anyway nothing, and stranding them costs them
          // their completion. The envelope is passed on exactly as it arrived: it carries no
          // `redirect_url`, so the caller falls back to the timed-out completion page.
          if (serverSaysExpired && result) {
            hardStopSentRef.current = true;
            onHardStopRef.current(result);
          }
          return;
        }
        // The stall watchdog may already have reopened the door and armed the next ask for this
        // same POST. Doing it again REPLACES that wake with a later one — and on a survey with no
        // clock of ours there is no 1Hz tick to carry the retry instead, so the ask is simply
        // pushed further out, every second of it a second in which answers are silently dropped.
        if (retryDecided) return;
        retryDecided = true;
        expiryRequestedRef.current = false;
        wakeForExpiryRetry();
      })
      // The HTTP-error half of the same contract the refusal list above covers. `expireSurveyTimer`
      // throws on any non-2xx, and a 404 — which is what this endpoint answers for a session that
      // has been deleted or purged — is as permanent as any reason it could have named in a 200.
      // Retried like a dropped packet it is a POST every thirty seconds for the life of the page,
      // on the unauthenticated respondent path, which is the hazard this module's own comments
      // name and the one path that never got the guard.
      .catch((error: unknown) => {
        if (isUnrecoverableRequestFailure(error)) {
          abandonExpiry();
          return;
        }
        askAgainLater();
      });
  }, [remaining, nowMs, config, sessionId, serverSaysExpired]);

  // Derived, so there is no dismiss timer to leak or race with the tick.
  const nudge = nudgeShowing(raised, nowMs) ? raised!.message : null;
  const readout = config ? readoutState(remaining, config) : { show: false, final: false };

  return {
    remaining,
    paused: Boolean(snapshot?.paused),
    showReadout: readout.show,
    isFinal: readout.final,
    nudge,
    ownsHeartbeat: active && heartbeatHealthy,
    activityRef,
  };
}
